import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Palette, Sparkles, ShoppingBag, ShoppingCart, Minus, Plus, LogIn } from 'lucide-react';
import SEO from '@/components/SEO';
import SocialStrip from '@/components/SocialStrip';
import ReportButton from '@/components/ReportButton';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const CATEGORY_TO_PRODUCT = {
  phone_case: 'pc-iphone-custom',
  sticker: 'st-vinyl-custom',
  label: 'lb-custom',
};

const CATEGORY_LABEL_FOR_MODEL = {
  phone_case: 'Product',
  sticker: 'Sticker sheet',
  label: 'Label sheet',
};

const DesignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { user } = useAuth();
  const { format } = useCurrency();
  const { t, tModel } = useLanguage();
  const { settings } = useSiteSettings();
  const storeOpen = settings.store_open !== false;
  const [design, setDesign] = useState(null);
  const [related, setRelated] = useState([]);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const [model, setModel] = useState('');
  const [variant, setVariant] = useState('');
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setDesign(null); setError(false);
    api.get(`/designs/${id}`).then(({ data }) => setDesign(data)).catch(() => setError(true));
  }, [id]);

  useEffect(() => {
    if (!design) return;
    api.get('/designs', { params: { category: design.category, page_size: 12 } })
      .then(({ data }) => setRelated(((data && data.items) || data || []).filter(x => x.id !== design.id).slice(0, 8)))
      .catch(() => setRelated([]));
    api.get('/products').then(({ data }) => setProducts(data || [])).catch(() => setProducts([]));
  }, [design]);

  // Compatible products = same category as design; exclude custom/one-off products.
  const compatibleProducts = useMemo(
    () => products.filter((p) => p.type === design?.category && !String(p.id || '').startsWith('custom-')),
    [products, design]
  );
  const selectedProduct = useMemo(
    () => compatibleProducts.find((p) => p.id === productId) || compatibleProducts[0],
    [compatibleProducts, productId]
  );

  // Sync productId + variant when the compatible list resolves.
  useEffect(() => {
    if (compatibleProducts.length && !productId) {
      const preferred = CATEGORY_TO_PRODUCT[design?.category];
      const pick = compatibleProducts.find((p) => p.id === preferred) || compatibleProducts[0];
      setProductId(pick.id);
    }
  }, [compatibleProducts, productId, design]);
  useEffect(() => {
    if (selectedProduct?.variants?.length && !selectedProduct.variants.includes(variant)) {
      setVariant(selectedProduct.variants[0]);
    }
  }, [selectedProduct, variant]);
  useEffect(() => {
    // reset model when product changes; default to the first configured model
    const first = (selectedProduct?.models || [])[0] || '';
    setModel(first);
  }, [selectedProduct]);

  const openInEditor = () => {
    if (!design) return;
    const targetProduct = productId || CATEGORY_TO_PRODUCT[design.category] || 'st-vinyl-custom';
    localStorage.setItem('jw_editor_prefill', JSON.stringify({
      image_url: assetUrl(design.image_url),
      design_id: design.id,
      category: design.category,
    }));
    navigate(`/design/${targetProduct}`);
  };

  const handleAddToCart = async () => {
    if (!storeOpen) {
      toast.error(t('closure.cannotAdd', "We're paused for new orders — you can browse but can't add to cart right now."));
      return;
    }
    if (!user) {
      toast(t('designDetail.signInAddCart', 'Sign in to add items to your cart'), {
        action: { label: t('nav.signIn', 'Sign in'), onClick: () => navigate('/login') },
      });
      return;
    }
    if (!selectedProduct) {
      toast.error(t('designDetail.noCompatibleProduct', 'This design has no compatible product yet.'));
      return;
    }
    setAdding(true);
    try {
      await add({
        product_id: selectedProduct.id,
        variant: [model, variant].filter(Boolean).join(' / ') || selectedProduct.variants?.[0] || '',
        model: model || undefined,
        design_id: design.id,
        quantity: qty,
      });
      toast.success(t('designDetail.addedToast', 'Added to cart'), {
        action: { label: t('designDetail.viewCart', 'View cart'), onClick: () => navigate('/cart') },
      });
    } catch (err) {
      console.error('Add to cart failed', err);
      toast.error(err?.response?.data?.detail || t('designDetail.couldNotAdd', 'Could not add to cart'));
    } finally {
      setAdding(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
        <Palette className="w-10 h-10 mx-auto opacity-40" />
        <h1 className="font-display text-2xl font-bold mt-4">{t('designDetail.notFound', 'Design not found')}</h1>
        <p className="mt-2 text-sm" style={{ color: 'var(--jw-text-muted)' }}>
          {t('designDetail.notFoundDesc', 'This design may have been removed. Try browsing the full library.')}
        </p>
        <Link to="/designs"><Button className="mt-6 jw-btn-glossy">{t('designDetail.backLibrary', 'Back to Design Library')}</Button></Link>
      </div>
    );
  }

  if (!design) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Skeleton className="h-8 w-40" />
        <div className="grid md:grid-cols-[1fr_360px] gap-6 mt-6">
          <Skeleton className="aspect-square rounded-2xl" />
          <div className="space-y-3">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const catLabel = (design.category || '').replace('_', ' ');

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <SEO
        title={`${design.name} — Jwalek Design Library`}
        description={`${design.name}: a ready-to-print ${catLabel} design from the Jwalek library. Pick this design and customize it in the editor.`}
        path={`/designs/${design.id}`}
      />

      <button onClick={() => navigate(-1)} data-testid="design-detail-back-button" className="inline-flex items-center gap-1 text-sm" style={{ color: 'var(--jw-text-muted)' }}>
        <ArrowLeft className="w-4 h-4 jw-flip-in-rtl" /> {t('common.back', 'Back')}
      </button>

      <div className="grid md:grid-cols-[1fr_360px] gap-6 mt-4">
        <Card className="jw-card overflow-hidden relative" data-testid="design-detail-image-card">
          <div className="absolute top-3 right-3 z-10">
            <ReportButton entityType="design" entityId={design.id} />
          </div>
          <div className="aspect-square w-full overflow-hidden grid place-items-center" style={{ background: 'var(--jw-bg-elev)' }}>
            <img
              src={assetUrl(design.image_url) || '/favicon.svg'}
              onError={(e) => { e.currentTarget.src = '/favicon.svg'; }}
              alt={design.name}
              className="max-w-full max-h-full object-contain"
              data-testid="design-detail-image"
            />
          </div>
        </Card>

        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary">{t(`designCategory.${design.category}`, catLabel)}</Badge>
              {design.is_premium && (
                <Badge className="jw-gradient-bg text-white border-transparent"><Sparkles className="w-3 h-3 mr-1" /> Premium</Badge>
              )}
            </div>
            <h1 className="font-display text-3xl font-bold mt-3 jw-text-primary" data-testid="design-detail-title">{design.name}</h1>
            {design.tags && (
              <p className="text-xs mt-2" style={{ color: 'var(--jw-text-subtle)' }}>
                {(Array.isArray(design.tags) ? design.tags : String(design.tags).split(',')).slice(0, 6).map(t => (
                  <span key={t} className="inline-block mr-2">#{String(t).trim()}</span>
                ))}
              </p>
            )}
          </div>

          <Card className="jw-card p-4">
            <SocialStrip entityType="design" entityId={design.id} layout="full" />
          </Card>

          <Card className="jw-card p-4 space-y-4" data-testid="design-detail-buy-card">
            {compatibleProducts.length > 0 && (
              <>
                <div>
                  <p className="text-xs mb-2 uppercase tracking-wide" style={{ color: 'var(--jw-text-muted)' }}>
                    {CATEGORY_LABEL_FOR_MODEL[design.category] || 'Product'}
                  </p>
                  <Select value={productId} onValueChange={setProductId}>
                    <SelectTrigger data-testid="design-detail-product-select" className="jw-panel h-11">
                      <SelectValue placeholder="Choose a product" />
                    </SelectTrigger>
                    <SelectContent>
                      {compatibleProducts.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`design-detail-product-option-${p.id}`}>
                          {p.name} — {format(p.price)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedProduct?.models?.length > 0 && (
                  <div>
                    <p className="text-xs mb-2 uppercase tracking-wide" style={{ color: 'var(--jw-text-muted)' }}>
                      {design.category === 'phone_case' ? t('product.iphoneModel', 'iPhone model') : design.category === 'label' ? t('product.shapeSize', 'Shape / size') : t('product.size', 'Size')}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.models.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setModel(m)}
                          data-selected={model === m ? 'true' : 'false'}
                          data-testid={`design-detail-model-${m.replace(/\s+/g, '-').toLowerCase()}-button`}
                          className="jw-chip"
                        >{tModel(m)}</button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedProduct?.variants?.length > 0 && (
                  <div>
                    <p className="text-xs mb-2 uppercase tracking-wide" style={{ color: 'var(--jw-text-muted)' }}>{t('product.finish', 'Finish')}</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.variants.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setVariant(v)}
                          data-selected={variant === v ? 'true' : 'false'}
                          data-testid={`design-detail-variant-${v.replace(/\s+/g, '-').toLowerCase()}-button`}
                          className="jw-chip"
                        >{tModel(v)}</button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-xs mb-2 uppercase tracking-wide" style={{ color: 'var(--jw-text-muted)' }}>{t('product.quantity', 'Quantity')}</p>
                    <div className="flex items-center gap-1 jw-panel rounded-lg overflow-hidden">
                      <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="design-detail-qty-decrement" className="w-9 h-9 grid place-items-center hover:opacity-80" aria-label="Decrease quantity"><Minus className="w-4 h-4" /></button>
                      <span data-testid="design-detail-qty-value" className="min-w-[36px] text-center text-sm font-semibold">{qty}</span>
                      <button type="button" onClick={() => setQty((q) => Math.min(99, q + 1))} data-testid="design-detail-qty-increment" className="w-9 h-9 grid place-items-center hover:opacity-80" aria-label="Increase quantity"><Plus className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {selectedProduct && (
                    <div className="ml-auto text-right">
                      <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--jw-text-subtle)' }}>{t('product.total', 'Total')}</p>
                      <p className="font-display text-lg font-bold jw-text-primary" data-testid="design-detail-total">
                        {format(((selectedProduct.model_pricing || {})[model] ?? selectedProduct.price ?? 0) * qty)}
                      </p>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleAddToCart}
                  disabled={adding || !selectedProduct || !storeOpen}
                  data-testid="design-detail-add-to-cart-button"
                  className="jw-btn-glossy w-full h-12 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {!storeOpen ? <><ShoppingCart className="w-4 h-4 mr-2" /> {t('closure.addToCartLabel', 'Orders paused')}</>
                    : !user ? <><LogIn className="w-4 h-4 mr-2" /> {t('designDetail.signInToAddButton', 'Sign in to add to cart')}</>
                    : adding ? t('designDetail.adding', 'Adding...')
                    : <><ShoppingCart className="w-4 h-4 mr-2" /> {t('product.addToCart', 'Add to cart')}</>}
                </Button>
              </>
            )}
          </Card>

          <div className="grid gap-2">
            <Button onClick={openInEditor} data-testid="design-detail-use-button" variant="secondary" className="w-full h-11 bg-white/[0.06] text-white border border-white/10 hover:bg-white/[0.09]">
              <Palette className="w-4 h-4 mr-2" /> {t('designDetail.customize', 'Customize in editor')}
            </Button>
            <Link to="/shop" className="w-full">
              <Button variant="ghost" data-testid="design-detail-shop-button" className="w-full h-10 jw-text-muted">
                <ShoppingBag className="w-4 h-4 mr-2" /> {t('designDetail.browseProducts', 'Browse products')}
              </Button>
            </Link>
          </div>

          <p className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>
            {t('designDetail.tip', 'Tip: pick "Customize in editor" if you want to tweak the design before ordering.')}
          </p>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-12" data-testid="design-detail-related">
          <h2 className="font-display text-xl font-bold mb-4 jw-text-primary">More {catLabel} designs</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {related.map(r => (
              <Link key={r.id} to={`/designs/${r.id}`} data-testid={`design-detail-related-${r.id}`}>
                <Card className="jw-card overflow-hidden group">
                  <div className="aspect-square overflow-hidden p-2" style={{ background: 'var(--jw-bg-elev)' }}>
                    <img
                      src={assetUrl(r.image_url) || '/favicon.svg'}
                      onError={(e) => { e.currentTarget.src = '/favicon.svg'; }}
                      alt={r.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-semibold jw-text-primary truncate">{r.name}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default DesignDetail;
